/**
 * Relaties Overzicht Pagina
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { relationsService, Relation } from '../../services/relations.service';
import { useAuth } from '../../context/AuthContext';
import ConfirmDialog from '../../components/ConfirmDialog';
import BoatChoiceDialog from '../../components/BoatChoiceDialog';
import ErrorDialog from '../../components/ErrorDialog';
import './Relations.css';

const RelationsList = () => {
  const [relations, setRelations] = useState<Relation[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const [confirmDelete, setConfirmDelete] = useState<{ id: number; naam: string } | null>(null);
  const [boatChoiceDialog, setBoatChoiceDialog] = useState<{ id: number; naam: string; message: string } | null>(null);
  const [sortField, setSortField] = useState<'naam' | 'bootnaam' | null>('naam');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Sticky header state
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [isHeaderSticky, setIsHeaderSticky] = useState(false);
  const [tableLeft, setTableLeft] = useState(0);
  const [tableWidth, setTableWidth] = useState(0);

  // Get filter type from URL
  const filterType = searchParams.get('type');
  const showBootnaam = false;

  const loadRelations = async () => {
    try {
      if (relations.length === 0) {
        setInitialLoading(true);
      } else {
        setSearching(true);
      }

      const response = await relationsService.getAll({
        zoek: debouncedSearchTerm || undefined,
        type: filterType || undefined,
        limit: 50,
        sorteer: sortField || 'naam',
        richting: sortDirection,
      });

      setRelations(response.items);
      setTotal(response.total);
      setHasMore(response.items.length === 50 && response.items.length < response.total);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen relaties');
    } finally {
      setInitialLoading(false);
      setSearching(false);
    }
  };

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load more function (defined before useEffect that uses it)
  const handleLoadMore = useCallback(async () => {
    if (searching || !hasMore) return;

    try {
      setSearching(true);
      const currentOffset = relations.length;

      const response = await relationsService.getAll({
        zoek: debouncedSearchTerm || undefined,
        type: filterType || undefined,
        limit: 50,
        offset: currentOffset,
      });

      setRelations(prev => [...prev, ...response.items]);
      setHasMore(response.items.length === 50 && (relations.length + response.items.length) < total);
    } catch (err: any) {
      setError(err.message || 'Fout bij ophalen relaties');
    } finally {
      setSearching(false);
    }
  }, [searching, hasMore, relations.length, debouncedSearchTerm, filterType, total]);

  // Load relations when debounced search term, filter, or sort changes
  useEffect(() => {
    loadRelations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, filterType, sortField, sortDirection]);

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      // Check if near bottom of page
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      // Load more when within 300px of bottom
      if (scrollHeight - scrollTop - clientHeight < 300 && hasMore && !searching && relations.length < total) {
        handleLoadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasMore, searching, relations.length, total, handleLoadMore]);

  // Sticky header handler
  useEffect(() => {
    if (!relations.length) return;

    const handleScroll = () => {
      if (tableHeaderRef.current) {
        const rect = tableHeaderRef.current.getBoundingClientRect();
        const shouldBeSticky = rect.bottom <= 185; // Show sticky when original scrolls to position 185
        setTableLeft(rect.left);
        setTableWidth(rect.width);
        setIsHeaderSticky(shouldBeSticky);
      }
    };

    const timeout = setTimeout(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', handleScroll);
      handleScroll();
    }, 100);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [relations]);

  const handleDeleteClick = (id: number, naam: string) => {
    setConfirmDelete({ id, naam });
  };

  const handleDeleteConfirm = async () => {
    if (!confirmDelete) return;

    try {
      // Probeer eerst normale delete zonder opties
      await relationsService.delete(confirmDelete.id);
      setConfirmDelete(null);
      loadRelations();
    } catch (err: any) {
      // Check of fout over gekoppelde boten gaat
      if (err.message && err.message.includes('gekoppelde boot')) {
        setConfirmDelete(null);
        setBoatChoiceDialog({
          id: confirmDelete.id,
          naam: confirmDelete.naam,
          message: err.message
        });
      } else {
        setError('Fout bij verwijderen: ' + err.message);
        setConfirmDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setConfirmDelete(null);
  };

  const handleDeleteBoats = async () => {
    if (!boatChoiceDialog) return;

    try {
      await relationsService.delete(boatChoiceDialog.id, { delete_boats: true });
      setBoatChoiceDialog(null);
      loadRelations();
    } catch (err: any) {
      setError('Fout bij verwijderen: ' + err.message);
      setBoatChoiceDialog(null);
    }
  };

  const handleSetOwnerUnknown = async () => {
    if (!boatChoiceDialog) return;

    try {
      await relationsService.delete(boatChoiceDialog.id, { set_owner_unknown: true });
      setBoatChoiceDialog(null);
      loadRelations();
    } catch (err: any) {
      setError('Fout bij verwijderen: ' + err.message);
      setBoatChoiceDialog(null);
    }
  };

  const handleBoatChoiceCancel = () => {
    setBoatChoiceDialog(null);
  };

  if (initialLoading) {
    return <div className="loading">Laden...</div>;
  }

  // Determine page title based on filter
  const getPageTitle = () => {
    if (filterType === 'klant') return 'Klanten';
    if (filterType === 'leverancier') return 'Leveranciers';
    if (filterType === 'zzp') return "ZZP'ers";
    return 'Relaties';
  };

  // Get display name based on relation type
  const getDisplayName = (relation: Relation) => {
    // For Particulier: show achternaam, voornaam tussenvoegsel
    if (relation.bedrijfsnaam === 'Particulier' && relation.achternaam) {
      const firstName = [
        relation.voornaam,
        relation.tussenvoegsel
      ].filter(Boolean).join(' ');
      return firstName ? `${relation.achternaam}, ${firstName}` : relation.achternaam;
    }
    // For all others (bedrijven): show bedrijfsnaam
    return relation.bedrijfsnaam;
  };

  // Get boot display - show boat name or "Meerdere" if multiple boats
  const getBootDisplay = (relation: Relation) => {
    if (!relation.boot_namen || relation.boot_namen.length === 0) {
      return '-';
    }
    if (relation.boot_namen.length === 1) {
      return relation.boot_namen[0];
    }
    return 'Meerdere';
  };

  // Format plaats - capitalize first letter of each word
  const formatPlaats = (plaats: string | null | undefined) => {
    if (!plaats) return '-';
    return plaats
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Handle sort
  const handleSort = (field: 'naam' | 'bootnaam') => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="relations-list">
      <div className="page-header">
        <h1>{getPageTitle()}</h1>
        {hasPermission('relaties_aanmaken') && (
          <button
            className="btn-lg btn-primary"
            onClick={() => navigate('/relations/new')}
          >
            + Nieuwe Relatie
          </button>
        )}
      </div>

      <div className="search-bar">
        <input
          type="text"
          placeholder="Zoek op naam, contactpersoon of email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        <span className="search-results">
          {searching && '🔍 '}
          {total} {total === 1 ? 'resultaat' : 'resultaten'}
        </span>
      </div>

      {/* Vaste header rij (altijd zichtbaar) */}
      {relations.length > 0 && (
        <div ref={tableHeaderRef} className="table-header" style={{
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          padding: '0.75rem 1rem',
          marginTop: '1rem',
          borderRadius: '8px 8px 0 0',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div onClick={() => handleSort('naam')} style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
            Naam {sortField === 'naam' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          {showBootnaam && (
            <div onClick={() => handleSort('bootnaam')} style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
              Bootnaam {sortField === 'bootnaam' && (sortDirection === 'asc' ? '↑' : '↓')}
            </div>
          )}
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Plaats</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Mobiel</div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Email</div>
          <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '1.3', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Sticky header overlay (duplicaat) */}
      {isHeaderSticky && (
        <div className="table-header" style={{
          position: 'fixed',
          top: '185px',
          left: `${tableLeft}px`,
          width: `${tableWidth}px`,
          zIndex: 1000,
          display: 'flex',
          backgroundColor: '#f8f9fa',
          borderBottom: '2px solid #dee2e6',
          borderRadius: '8px 8px 0 0',
          padding: '0.75rem 1rem',
          fontWeight: 600,
          fontSize: '0.875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          color: '#495057'
        }}>
          <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem' }}>Type</div>
          <div onClick={() => handleSort('naam')} style={{ flex: '1.5', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
            Naam {sortField === 'naam' && (sortDirection === 'asc' ? '↑' : '↓')}
          </div>
          {showBootnaam && (
            <div onClick={() => handleSort('bootnaam')} style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem', cursor: 'pointer' }}>
              Bootnaam {sortField === 'bootnaam' && (sortDirection === 'asc' ? '↑' : '↓')}
            </div>
          )}
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Plaats</div>
          <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem' }}>Mobiel</div>
          <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem' }}>Email</div>
          <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem' }}>Status</div>
          <div style={{ flex: '1.3', minWidth: 0 }}>Acties</div>
        </div>
      )}

      {/* Relaties lijst - Desktop */}
      {relations.length > 0 && (
        <div className="desktop-table" style={{
          background: 'white',
          borderRadius: '0 0 8px 8px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}>
          {relations.map(relation => (
            <div key={relation.id} style={{
              display: 'flex',
              alignItems: 'center',
              padding: '0.75rem 1rem',
              borderBottom: '1px solid #dee2e6',
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
            >
              <div style={{ flex: '0.8', minWidth: 0, paddingRight: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span className={`badge badge-${relation.type.toLowerCase()}`}>
                  {relation.type}
                </span>
                {relation.is_zzp && (
                  <span className="badge badge-zzp" style={{ backgroundColor: '#10b981', color: 'white' }}>
                    ZZP
                  </span>
                )}
              </div>
              <div style={{ flex: '1.5', minWidth: 0, fontWeight: 600, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getDisplayName(relation)}</div>
              {showBootnaam && (
                <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getBootDisplay(relation)}</div>
              )}
              <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{formatPlaats(relation.plaats)}</div>
              <div style={{ flex: '1', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{relation.mobiel || relation.telefoon || '-'}</div>
              <div style={{ flex: '1.2', minWidth: 0, paddingRight: '1rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{relation.email || '-'}</div>
              <div style={{ flex: '0.7', minWidth: 0, paddingRight: '1rem', display: 'flex', alignItems: 'center' }}>
                <span className={`status ${relation.actief ? 'active' : 'inactive'}`}>
                  {relation.actief ? 'Actief' : 'Inactief'}
                </span>
              </div>
              <div className="actions" style={{ flex: '1.3', minWidth: 0 }}>
                <button
                  className="btn-small btn-primary"
                  onClick={() => navigate(`/relations/${relation.id}`)}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  Bekijk
                </button>
                {hasPermission('relaties_verwijderen') && (
                  <button
                    className="btn-small btn-danger"
                    onClick={() => handleDeleteClick(relation.id, getDisplayName(relation))}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    Verwijder
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Relaties lijst - Mobile tegels */}
      {relations.length > 0 && (
        <div className="mobile-cards">
          {relations.map(relation => (
            <div key={relation.id} className="relation-card">
              <div className="relation-card-header">
                <span className="relation-name">{getDisplayName(relation)}</span>
                <span className={`badge badge-${relation.type.toLowerCase()}`}>{relation.type}</span>
              </div>
              <div className="relation-card-body">
                {showBootnaam && getBootDisplay(relation) !== '-' && (
                  <div className="relation-info"><span className="label">Boot:</span> {getBootDisplay(relation)}</div>
                )}
                {relation.plaats && (
                  <div className="relation-info"><span className="label">Plaats:</span> {formatPlaats(relation.plaats)}</div>
                )}
                {(relation.mobiel || relation.telefoon) && (
                  <div className="relation-info"><span className="label">Tel:</span> {relation.mobiel || relation.telefoon}</div>
                )}
                {relation.email && (
                  <div className="relation-info"><span className="label">Email:</span> {relation.email}</div>
                )}
              </div>
              <div className="relation-card-actions">
                {(relation.mobiel || relation.telefoon) && (
                  <a
                    href={`tel:${relation.mobiel || relation.telefoon}`}
                    className="btn-small btn-success"
                    style={{ textDecoration: 'none' }}
                  >
                    Bellen
                  </a>
                )}
                <button
                  className="btn-small btn-primary"
                  onClick={() => navigate(`/relations/${relation.id}`)}
                >
                  Bekijk
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {relations.length === 0 && !initialLoading && (
        <div style={{
          padding: '3rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          textAlign: 'center',
          marginTop: '1rem'
        }}>
          Geen relaties gevonden
        </div>
      )}

      {searching && relations.length > 0 && (
        <div style={{ textAlign: 'center', padding: '1rem', color: '#666' }}>
          Meer laden...
        </div>
      )}

      {!hasMore && relations.length >= total && relations.length > 0 && (
        <div style={{ textAlign: 'center', padding: '1rem', color: '#999' }}>
          Alle resultaten geladen ({total} {total === 1 ? 'resultaat' : 'resultaten'})
        </div>
      )}

      <ConfirmDialog
        isOpen={!!confirmDelete}
        title="Relatie Verwijderen"
        message={`Weet je zeker dat je "${confirmDelete?.naam}" wilt verwijderen?`}
        confirmText="Verwijderen"
        cancelText="Annuleren"
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        danger={true}
      />

      <BoatChoiceDialog
        isOpen={!!boatChoiceDialog}
        message={boatChoiceDialog?.message || ''}
        onDeleteBoats={handleDeleteBoats}
        onSetUnknown={handleSetOwnerUnknown}
        onCancel={handleBoatChoiceCancel}
      />

      <ErrorDialog
        isOpen={!!error}
        message={error || ''}
        onClose={() => setError(null)}
      />
    </div>
  );
};

export default RelationsList;